// Rich-clipboard hyperlink capture — shared by the staff "Paste to create a
// Lead" modal (pages/leads/PasteToCreateLeadModal) and the Lead Generation
// portal paste box (portal/tabs/leadGen/PreleadsTab), which held byte-identical
// copies of this logic. Pure and field-agnostic: it reads the text/html and
// text/plain payloads already in hand from a ClipboardEvent. No React, no async
// clipboard read, no network — so both surfaces can share it and the portal
// bundle stays isolated from staff state.

/** One captured hyperlink from a rich (HTML) paste. */
export interface CapturedLink {
  label: string;
  href: string;
}

export interface ExtractLinksOptions {
  /** Drop obviously-boilerplate links (social profiles, cookie/terms/login
   *  pages). The Lead Generation paste box turns this ON so its preview isn't
   *  flooded with a listing site's chrome; the staff lead modal leaves it OFF
   *  and keeps every link it finds. Defaults to OFF. */
  dropBoilerplate?: boolean;
}

/** Append `next` onto `prev`, skipping any href already present. Preserves
 *  `prev`'s order and its existing entry (and therefore its label) on a
 *  collision, so re-pasting never renames an already-captured link. */
export function mergeLinks(prev: CapturedLink[], next: CapturedLink[]): CapturedLink[] {
  const seen = new Set(prev.map((l) => l.href));
  const merged = [...prev];
  for (const l of next) {
    if (!seen.has(l.href)) {
      seen.add(l.href);
      merged.push(l);
    }
  }
  return merged;
}

// A small, conservative denylist to drop obviously-boilerplate links from the
// composer PREVIEW only — the SERVER runs the authoritative listing-pertinence
// filter over capturedLinksJson. Domain tokens match the host exactly (or a
// subdomain) so e.g. dropbox.com is never mistaken for x.com.
const SOCIAL_DOMAINS = [
  "instagram.com", "youtube.com", "youtu.be", "facebook.com", "fb.com",
  "linkedin.com", "twitter.com", "x.com", "tiktok.com", "pinterest.com", "reddit.com",
];
const BOILERPLATE_PATH_MARKERS = [
  "/cookie", "/privacy", "/terms", "terms-of", "/sitemap", "/blog", "/signin",
  "/sign-in", "/login", "/register", "/signup", "/join", "/advertise", "/subscribe", "/careers",
];
function isBoilerplateLink(href: string): boolean {
  const lower = href.toLowerCase();
  let host = lower;
  try {
    host = new URL(lower).hostname;
  } catch {
    /* keep the whole lowercased href as the host fallback */
  }
  if (SOCIAL_DOMAINS.some((d) => host === d || host.endsWith(`.${d}`))) return true;
  return BOILERPLATE_PATH_MARKERS.some((p) => lower.includes(p));
}

/** Pull every hyperlink out of a rich (HTML) clipboard payload, augmented with
 *  a plain-text URL regex. Runs entirely from the already-in-hand
 *  ClipboardEvent data — no async clipboard read, no network. */
export function extractLinks(
  html: string,
  plain: string,
  options: ExtractLinksOptions = {},
): CapturedLink[] {
  const dropBoilerplate = options.dropBoilerplate === true;
  const links: CapturedLink[] = [];
  const seen = new Set<string>();
  const push = (href: string, label: string) => {
    const clean = href.trim();
    if (!/^https?:\/\//i.test(clean) || seen.has(clean)) return;
    if (dropBoilerplate && isBoilerplateLink(clean)) return;
    seen.add(clean);
    links.push({ href: clean, label: (label || clean).trim().slice(0, 200) });
  };
  if (html) {
    try {
      const doc = new DOMParser().parseFromString(html, "text/html");
      doc.querySelectorAll("a[href]").forEach((a) => {
        push(a.getAttribute("href") ?? "", a.textContent ?? "");
      });
    } catch {
      /* malformed HTML — fall through to the plain-text regex */
    }
  }
  const urlRe = /https?:\/\/[^\s<>"')]+/gi;
  let m: RegExpExecArray | null;
  while ((m = urlRe.exec(plain)) !== null) push(m[0], m[0]);
  return links;
}
